import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  LifebuoyIcon,
  EnvelopeIcon,
  PhoneIcon,
  BellIcon,
  BuildingOfficeIcon,
  ArrowUpCircleIcon,
} from '@heroicons/react/24/outline';
import {
  getTenants,
  getNotificationContacts,
  updateNotificationContacts,
} from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';
import { phoneError } from '../utils/phone';
import PhoneField from '../components/common/PhoneField';
import { useTenantPath } from '../hooks/useTenantPath';

const TIER_LABELS = { starter: 'Starter', growth: 'Growth', scale: 'Scale' };

export default function SupportNotificationsPage() {
  const { tenantId: urlTenantId } = useParams();
  const user = useAuthStore((s) => s.user);
  const isSuperadmin = user?.is_superuser || user?.role === 'superadmin';

  const [tenantList, setTenantList] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(urlTenantId || (!isSuperadmin ? user?.tenant_id : ''));
  const tenantId = selectedTenantId;
  const tenantPath = useTenantPath();

  const [loading, setLoading] = useState(true);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [tier, setTier] = useState('growth');
  const [supportAvailable, setSupportAvailable] = useState(true);

  // ── Tenant list for superadmin ─────────────────────────────────
  useEffect(() => {
    if (!isSuperadmin) return;
    (async () => {
      try {
        setTenantsLoading(true);
        const data = await getTenants(true);
        setTenantList(data);
        if (!selectedTenantId && data.length > 0) setSelectedTenantId(data[0].id);
      } catch (err) {
        toast.error('Failed to load tenants');
      } finally {
        setTenantsLoading(false);
      }
    })();
  }, [isSuperadmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load support settings ──────────────────────────────────────
  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await getNotificationContacts(tenantId);
      const prefs = data.notification_preferences || {};
      setSupportEmail(prefs.support_email || '');
      setSupportPhone(prefs.support_phone || '');
      setRemindersEnabled(prefs.support_tickets_enabled !== false);
      setTier(data.tier);
      setSupportAvailable(Boolean((data.notification_config || {}).support_tickets));
    } catch (err) {
      toast.error('Failed to load support notification settings');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // The number is optional, but if one is given it must carry a country code —
  // Meta rejects support alerts sent to a bare local number.
  const supportPhoneError = supportPhone.trim() ? phoneError(supportPhone) : null;

  // ── Save ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (supportEmail && !/\S+@\S+\.\S+/.test(supportEmail)) {
      toast.error('Enter a valid support email address');
      return;
    }
    if (supportPhoneError) {
      toast.error(supportPhoneError);
      return;
    }
    try {
      setSaving(true);
      await updateNotificationContacts(tenantId, {
        notification_preferences: {
          support_email: supportEmail,
          support_phone: supportPhone,
          support_tickets_enabled: remindersEnabled,
        },
      });
      toast.success('Support notification settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save support settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || tenantsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const disabled = !supportAvailable;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <LifebuoyIcon className="h-7 w-7 sm:h-8 sm:w-8 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Support Notifications</h1>
          <p className="text-xs sm:text-sm text-gray-500">
            Where your team is alerted about customer support tickets, and where resolved-issue
            emails reply back to.
          </p>
        </div>
      </div>

      {/* Tenant selector for superadmin */}
      {isSuperadmin && (
        <div className="rounded-lg bg-white border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
            <label htmlFor="tenant-select" className="text-sm font-semibold text-gray-700">Select Tenant</label>
          </div>
          <select
            id="tenant-select"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
          >
            <option value="">— Select a tenant —</option>
            {tenantList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug}) — {(t.subscription_tier || 'growth').charAt(0).toUpperCase() + (t.subscription_tier || 'growth').slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {!tenantId ? (
        <div className="text-center py-12 text-gray-400">
          <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">Select a tenant above to manage support notifications.</p>
        </div>
      ) : (
      <>
        {/* HelpDesk is a separately purchased capability on every tier. */}
        {disabled && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
            <ArrowUpCircleIcon className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1 text-sm text-amber-800">
              <p className="font-semibold">The AI HelpDesk add-on is not active.</p>
              <p className="mt-1 text-amber-700">
                HelpDesk is a separate add-on on every plan. Add it to let your AI agent answer
                post-sale questions, capture support tickets, and alert your team automatically.
                {' '}Your current plan is {TIER_LABELS[tier] || tier}.
              </p>
              {!isSuperadmin && (
                <Link
                  to={tenantPath('/billing')}
                  className="mt-3 inline-flex rounded-md bg-amber-700 px-3 py-2 font-semibold text-white hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2 focus:ring-offset-amber-50"
                >
                  View HelpDesk add-on
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Support inbox email */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <EnvelopeIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Support Inbox Email</h2>
          </div>
          <div className="p-5 space-y-2">
            <input
              type="email"
              placeholder="support@yourbusiness.com"
              value={supportEmail}
              disabled={disabled}
              onChange={(e) => setSupportEmail(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-500">
              New tickets and open-ticket reminders are emailed here. It's also the reply-to address on
              the "issue resolved" email your customer receives, so their replies reach your team.
              If left blank, alerts fall back to your lead notification contacts.
            </p>
          </div>
        </section>

        {/* Team WhatsApp number — live via approved support template */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <PhoneIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Team WhatsApp Number</h2>
          </div>
          <div className="p-5 space-y-2">
            <PhoneField
              value={supportPhone}
              disabled={disabled}
              onChange={setSupportPhone}
              selectClassName="w-36 shrink-0 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm shadow-sm disabled:bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
              inputClassName={`min-w-0 flex-1 rounded-md shadow-sm text-sm px-3 py-2 border disabled:bg-gray-100 disabled:text-gray-500 ${
                supportPhoneError
                  ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
              }`}
            />
            {supportPhoneError && (
              <p className="text-xs text-red-500">{supportPhoneError}</p>
            )}
            <p className="text-xs text-gray-500">
              The number where your team is notified about support tickets. Sent via WhatsApp the moment
              a ticket is raised, plus reminder pings at 2 hours and 24 hours if still open. Email alerts
              continue in parallel as a fallback.
            </p>
          </div>
        </section>

        {/* Open-ticket reminders opt-out */}
        <section className="rounded-lg bg-white border border-gray-200 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <BellIcon className="h-6 w-6 text-amber-600 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Open-Ticket Reminders</h2>
              <p className="text-sm text-gray-500 mt-1">
                Your team is alerted the moment a ticket is raised, then re-nudged at 2 hours and again
                at 24 hours if it's still open. Available while the AI HelpDesk add-on is active.
              </p>
            </div>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={remindersEnabled}
              disabled={disabled}
              onChange={(e) => setRemindersEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-800">
              {remindersEnabled
                ? 'Enabled — your team is nudged at 2h and 24h on open tickets'
                : 'Disabled — only the alert when a ticket is first raised fires'}
            </span>
          </label>
        </section>

        {/* Save — full-width on mobile, right-aligned on desktop */}
        <div className="flex justify-stretch sm:justify-end">
          <button
            onClick={handleSave}
            disabled={saving || disabled || !!supportPhoneError}
            title={supportPhoneError || undefined}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              'Save Support Settings'
            )}
          </button>
        </div>
      </>
      )}
    </div>
  );
}
